import { PortalError } from "@/types/portal-error";
import { optionsHeadersContentMultipart, Service } from "./mainService";
import { AvisosResponse } from "@/types/avisos/avisos-response";
import { FiltrosAvisos } from "@/types/avisos/filtros-avisos";
import { UnidadeGestoraResumida } from "@/types/ug/unidade-gestora-resumida";
import { DepartamentoResumido } from "@/types/administracao/departamento-resumido";
import { Aviso } from "@/types/avisos/aviso";
import { AvisoResponse } from "@/types/avisos/aviso-response";

const messagesService = {
  mostrarAlertaCP,
  getAvisosUsuario,
  getDepartamentosOrigemAvisos,
  inserirVisualizacaoAviso,
  responderAviso,
  buscarResposta,
  getTodosAvisos,
  getTiposUg,
  getTiposAviso,
  filtrarAvisos,
  getAviso,
  getDestinatarios,
  removerDestinatario,
  getEsferas,
  getAreasCi,
  filtrarUgs,
  adicionarDestinatarios,
  filtrarUsuarios,
  getAvisosALiberar,
  editarAviso,
  adicionarAnexos,
  getAnexosAviso,
  baixarAnexo,
  removerAnexo,
  criarAviso,
  inativarAviso,
  liberarAviso,
  excluirTodosDestinatarios,
};

async function mostrarAlertaCP() {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API + "/avisos/mostrarAlertaCP"
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function getAvisosUsuario(): Promise<Aviso[]> {
  const url = `${process.env.VUE_APP_MESSAGES_API}/avisos/listarAvisos`;
  const response = await Service.$http.get(url);
  if (response && response.status == 200)
    return response.data.map((a) => new Aviso(a));
  console.error("Erro ao buscar avisos", response);
  throw new PortalError("Erro ao buscar avisos");
}

async function inserirVisualizacaoAviso(
  idAviso: number,
  idDestinatario: number
) {
  try {
    const data = new FormData();
    data.append("idAviso", String(idAviso));
    data.append("idDestinatario", String(idDestinatario));
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API + "/avisos/inserirVisualizacaoAviso",
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function responderAviso(
  mensagem: string,
  arquivos: any[],
  idAviso: number
) {
  try {
    const data = new FormData();
    data.append("mensagem", mensagem);
    let i = 0;
    for (i; i < arquivos.length; i++)
      data.append("arquivosResposta", arquivos[i]);
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API + `/avisos/responder/${idAviso}`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function buscarResposta(idAviso: number) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API + `/avisos/buscarresposta/${idAviso}`
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

/**
 * @returns Any[] Lista de departamentos que podem ser usados para originar um aviso.
 * @since 20240705
 */
async function getDepartamentosOrigemAvisos(): Promise<DepartamentoResumido[]> {
  const response = await Service.$http.get(
    process.env.VUE_APP_MESSAGES_API + `/avisos/departamentos`
  );
  if (!response || response.status !== 200) {
    console.error("Falha ao buscar departamentos", response);
    throw new PortalError(
      response.data?.msg ?? "Falha ao buscar departamentos"
    );
  }
  //console.log("messagesSrvice.getDepartamentosOrigemAvisos", response);
  return response.data.map((dr) => new DepartamentoResumido(dr));
}

async function getTodosAvisos(
  page: number,
  pageSize: number,
  sortBy?: string,
  sortDirection: "asc" | "desc" = "desc"
): Promise<AvisosResponse> {
  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });

  if (sortBy) {
    query.set("sortBy", sortBy);
    query.set("sortDirection", sortDirection);
    query.set("sortDesc", String(sortDirection === "desc"));
  }

  const response = await Service.$http.get(
    `${process.env.VUE_APP_MESSAGES_API}/avisos/listarTodosAvisos?${query.toString()}`
  );
  if (!response || response.status !== 200) {
    console.error("Erro ao buscar avisos", response);
    throw new PortalError("Erro ao buscar avisos");
  }
  return new AvisosResponse(response.data);
}

async function getTiposUg() {
  const response = await Service.$http.get(
    process.env.VUE_APP_MESSAGES_API + "/avisos/tiposUg"
  );
  if (!response || response.status !== 200) {
    console.error("Erro ao buscar tipos de UG", response);
    throw new PortalError("Erro ao buscar tipos de UG");
  }
  return response;
}

async function getTiposAviso() {
  const response = await Service.$http.get(
    process.env.VUE_APP_MESSAGES_API + "/avisos/tiposAviso"
  );
  if (!response || response.status !== 200) {
    console.error("Erro ao buscar tipos de aviso", response);
    throw new PortalError("Erro ao buscar tipos de aviso");
  }
  return response;
}

async function filtrarAvisos(
  page: number,
  pageSize: number,
  filtros: FiltrosAvisos,
  sortBy?: string,
  sortDirection: "asc" | "desc" = "desc"
): Promise<AvisosResponse> {
  const data = filtros?.toFormData() ?? new FormData();
  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });

  if (sortBy) {
    query.set("sortBy", sortBy);
    query.set("sortDirection", sortDirection);
    query.set("sortDesc", String(sortDirection === "desc"));
  }

  const url = `${process.env.VUE_APP_MESSAGES_API}/avisos/filtrarAvisos?${query.toString()}`;
  const response = await Service.$http.post(
    url,
    data,
    optionsHeadersContentMultipart
  );
  if (!response || response.status !== 200) {
    console.error("Falha ao filtrar avisos", response);
    throw new PortalError("Falha ao filtrar os avisos");
  }
  return new AvisosResponse(response.data);
}

async function getAviso(id: number): Promise<AvisoResponse> {
  const response = await Service.$http.get(
    process.env.VUE_APP_MESSAGES_API + "/avisos/detalhes/" + id
  );
  if (!response || response.status !== 200) {
    console.error("Erro ao buscar aviso", response);
    throw new PortalError("Erro ao buscar aviso");
  }
  return new AvisoResponse(response.data);
}

async function getDestinatarios(id: number) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API + "/avisos/destinatarios/" + id
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function removerDestinatario(idDestinatario: number) {
  try {
    const response = await Service.$http.delete(
      process.env.VUE_APP_MESSAGES_API +
        "/avisos/removerDestinatario/" +
        idDestinatario
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function getEsferas() {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API + "/avisos/esferas"
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function getAreasCi() {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API + "/avisos/areasci"
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function filtrarUgs(
  nome: string | null = null,
  municipio: string | null = null,
  esfera: string | null = null,
  tipoUg: string | null = null,
  areaCI: string | null = null
): Promise<UnidadeGestoraResumida[]> {
  const data = new FormData();
  if (nome) data.append("nome", nome);
  if (municipio) data.append("municipio", municipio);
  if (esfera) data.append("esfera", esfera);
  if (tipoUg) data.append("tipoUg", tipoUg);
  if (areaCI) data.append("areaCI", areaCI);

  const response = await Service.$http.post(
    process.env.VUE_APP_MESSAGES_API + "/avisos/filtrarUgs",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  if (!response || response.status !== 200) {
    console.error(response);
    throw new PortalError("Erro ao filtrar UGs");
  }
  return response.data
    .filter((f) => f.Id && f.Nome)
    .map((r) => new UnidadeGestoraResumida(r));
}

async function filtrarUsuarios(nome) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API + "/avisos/filtrarUsuarios?nome=" + nome
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function adicionarDestinatarios(
  idAviso,
  idsDestinatarios,
  tipoDestinatario
) {
  try {
    const data = new FormData();
    if (idsDestinatarios) {
      let i = 0;
      for (i; i < idsDestinatarios.length; i++)
        data.append("idsDestinatarios[" + i + "]", idsDestinatarios[i]);
    }
    if (tipoDestinatario) data.append("tipoDestinatario", tipoDestinatario);

    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API +
        "/avisos/adicionarDestinatarios/" +
        idAviso,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function getAvisosALiberar(): Promise<Aviso[]> {
  const response = await Service.$http.get(
    process.env.VUE_APP_MESSAGES_API + "/avisos/pendentes"
  );
  if (response.status !== 200) {
    console.error("Erro ao buscar avisos a liberar", response);
    throw new PortalError("Erro ao buscar avisos a liberar");
  }
  return response.data.map((a) => new Aviso(a));
}

async function editarAviso(aviso) {
  try {
    const data = aviso;
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API + "/avisos/editar",
      data
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}
/*
Params:
arquivos - lista de arquivos a serem anexados
id - id do aviso ou da resposta
tipo - 'aviso' ou 'resposta'
 */
async function adicionarAnexos(arquivos, id, tipo) {
  try {
    const data = new FormData();
    data.append("tipo", tipo);
    let i = 0;
    for (i; i < arquivos.length; i++) data.append("arquivos", arquivos[i]);
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API + "/avisos/adicionaranexos/" + id,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function getAnexosAviso(id: number) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API + "/avisos/anexos/" + id
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function baixarAnexo(id: number) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API + "/avisos/baixaranexo/" + id,
      { responseType: "blob" }
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function removerAnexo(id: number) {
  try {
    const response = await Service.$http.delete(
      process.env.VUE_APP_MESSAGES_API + "/avisos/removeranexo/" + id
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function criarAviso(aviso) {
  try {
    const data = aviso;
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API + "/avisos/criar",
      data
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function inativarAviso(id: number) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API + "/avisos/inativar/" + id
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function liberarAviso(id: number) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API + "/avisos/liberar/" + id
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function excluirTodosDestinatarios(idAviso: number) {
  const response = await Service.$http.delete(
    process.env.VUE_APP_MESSAGES_API +
      "/avisos/removerTodosDestinatarios/" +
      idAviso
  );
  if (response.status === 200) return response.data;
  console.error("erro ao excluir todos os destinatários do aviso", response);
  throw new Error("erro ao excluir todos os destinatários do aviso");
}

export default messagesService;
